import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  Box,
  Button,
  IconButton,
  Text,
  styled,
} from '@ltvco/refresh-lib/theme';
import { Close } from '@mui/icons-material';
import { useContext, useEffect, useRef, useState } from 'react';

import { useCallerIdSmokeTest } from './CallerIdSmokeTestContext';
import { CALLER_ID_COPY } from './types';

/**
 * Vertical scroll metrics for impression gating (analytics), not navigation.
 * refresh-lib `scrollToElement` scrolls to a section id — different concern.
 */
function getVerticalScrollMetrics(): { maxScroll: number; scrollY: number } {
  const root = document.documentElement;
  return {
    maxScroll: Math.max(0, root.scrollHeight - window.innerHeight),
    scrollY: window.scrollY,
  };
}

const CallerIdStickyBarShell = styled(Box)(({ theme }) => ({
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 68, // ReportNavigationMobileMenu height
  zIndex: 16, // Above report nav (15)
  padding: theme.spacing(1.5, 0.5, 1.5, 2),
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[4],
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const CallerIdStickyBarCopy = styled(Text)({
  flex: 1,
  minWidth: 0,
});

const CallerIdStickyBarCta = styled(Button)({
  flexShrink: 0,
  whiteSpace: 'nowrap',
});

const CallerIdStickyBarDismiss = styled(IconButton)(({ theme }) => ({
  flexShrink: 0,
  color: theme.palette.text.secondary,
}));

/**
 * Sticky bottom Smart Caller ID bar for mobile (RFRSH-4427).
 */
export function CallerIdStickyBar(): JSX.Element | null {
  const {
    isStickyBarVisible,
    stickyUnit,
    openComingSoon,
    dismissStickyBar,
    gaEventBase,
  } = useCallerIdSmokeTest();
  const { trackEventGA4 } = useContext(AppConfig);
  const impressionLogged = useRef(false);
  const [hasSubmittedGA4, setHasSubmittedGA4] = useState(false);

  useEffect(() => {
    // Fixed UI stays viewport-visible; IO would fire on mount. Use scroll depth instead.
    if (!isStickyBarVisible || impressionLogged.current || hasSubmittedGA4) {
      return;
    }

    const logImpression = (): void => {
      if (impressionLogged.current || hasSubmittedGA4) {
        return;
      }
      impressionLogged.current = true;
      trackEventGA4({
        ...gaEventBase,
        event_name: 'caller_id_cta_impression',
        cta_variant: stickyUnit,
        placement: 'sticky',
      });
      setHasSubmittedGA4(true);
    };

    const onScrollOrLayout = (): void => {
      if (impressionLogged.current || hasSubmittedGA4) {
        return;
      }
      const { maxScroll, scrollY } = getVerticalScrollMetrics();
      const shortPage = maxScroll < 200;
      const scrolledEnough = scrollY >= 200;
      if (shortPage || scrolledEnough) {
        logImpression();
        window.removeEventListener('scroll', onScrollOrLayout);
      }
    };

    window.addEventListener('scroll', onScrollOrLayout, { passive: true });
    onScrollOrLayout();

    return () => {
      window.removeEventListener('scroll', onScrollOrLayout);
    };
  }, [
    gaEventBase,
    hasSubmittedGA4,
    isStickyBarVisible,
    stickyUnit,
    trackEventGA4,
  ]);

  if (!isStickyBarVisible) {
    return null;
  }

  const copy = CALLER_ID_COPY[stickyUnit];

  return (
    <CallerIdStickyBarShell>
      <CallerIdStickyBarCopy variant="body2">{copy.body}</CallerIdStickyBarCopy>
      <CallerIdStickyBarCta
        variant="contained"
        color="primary"
        size="small"
        onClick={() => openComingSoon(stickyUnit, 'sticky')}
      >
        {copy.cta}
      </CallerIdStickyBarCta>
      <CallerIdStickyBarDismiss
        aria-label="Dismiss Smart Caller ID promo"
        size="small"
        edge="end"
        onClick={dismissStickyBar}
      >
        <Close sx={{ fontSize: 18 }} />
      </CallerIdStickyBarDismiss>
    </CallerIdStickyBarShell>
  );
}
